import React, {Fragment, useCallback} from 'react';
import {Button, ButtonProps, Divider, Grid2} from '@mui/material';
import {APIError} from 'data/errors';
import {ScheduleScriptOptionTypeEnum} from 'domain/usecases/private/booking-flow/health/get-questions-schedule-script';
import {AlertModal} from 'presentation/components';
import {HealthDuplicityDialog} from 'presentation/modules/booking/health/ui/pages/HealthBooking/steps/HealthSpecialtySelection/components/HealthDuplicityDialog';
import {ParsedHealthAppointment} from 'presentation/pages/private/home/utils/parseHealthAppointments/types';
import {translations} from 'presentation/translations-new';
import {CheckBookingModalEnum} from '../../hooks/useBookingSpecialty/types';
import {BubbleContainer} from './components/BubbleContainer';
import {StyledDescription, StyledSubDescription} from './components/BubbleContainer/styles';
import {DotLoading} from './components/DotLoading';
import {useScheduleScriptV2} from './hooks/useScheduleScriptV2';
import {StyledContainer} from './styles';
import {ScheduleScriptV2Props} from './types';
import {getOptionDataTagId, getQuestionDataTagId} from './utils/scriptTagUtils';

const {loadingError, tryAgainPlease} = translations['pt-br'].privateFlow.bookingFlow.scheduleScript;
const {tryAgain, goBack, serverError, serverErrorTitle, close, notice} =
	translations['pt-br'].commons;

export function ScheduleScriptV2({handleNext}: ScheduleScriptV2Props) {
	const {
		containerRef,
		messages,
		checkedAnswerOptions,
		openErrorModal,
		isPending,
		isError,
		error,
		greetings,
		isActionButton,
		handleCloseErrorModal,
		handleChangeMessage,
		handleRetryRequest,
		handleRestartConversation,
		scheduledBooking,
		checkBookingModal,
		resetBookingDataSpecialties,
		setCheckBookingModal,
	} = useScheduleScriptV2({handleNext});

	const renderClientBubble = useCallback(
		(msgIdx: number) => {
			const checkedOption = checkedAnswerOptions.find(option => option.idx === msgIdx);
			if (!checkedOption) return null;
			return (
				<BubbleContainer
					data-tag-id={getOptionDataTagId(
						checkedOption.text,
						ScheduleScriptOptionTypeEnum.FLOW,
					)}
					isClient>
					<StyledDescription $isClient>{checkedOption.text}</StyledDescription>
				</BubbleContainer>
			);
		},
		[checkedAnswerOptions],
	);

	const renderDefaultBubble = useCallback(
		(msg: (typeof messages)[number], msgIdx: number) => {
			const isAction = isActionButton(msg?.answerOptions);
			return (
				<BubbleContainer>
					<StyledDescription data-tag-id={getQuestionDataTagId(msg.description)}>
						{msg.description}
					</StyledDescription>
					{msg?.answerOptions?.length && !checkedAnswerOptions[msgIdx - 1] ? (
						<Grid2 container spacing={isAction ? 1 : 1 / 2} direction="column">
							{msg.answerOptions.map((option, optionIdx) => {
								let variant: ButtonProps['variant'];

								if (!isAction) {
									variant = 'text';
								} else if (optionIdx === 0) {
									variant = 'contained';
								} else {
									variant = 'outlined';
								}

								return (
									<Fragment key={optionIdx}>
										{optionIdx === 0 ? null : (
											<Grid2>
												<Divider />
											</Grid2>
										)}
										<Grid2>
											<Button
												sx={{
													whiteSpace: 'normal',
												}}
												data-tag-id={getOptionDataTagId(
													option,
													ScheduleScriptOptionTypeEnum.CHAT,
												)}
												variant={variant}
												fullWidth
												onClick={() => handleChangeMessage(option)}>
												{option}
											</Button>
										</Grid2>
									</Fragment>
								);
							})}
						</Grid2>
					) : null}
				</BubbleContainer>
			);
		},
		[checkedAnswerOptions, isActionButton, handleChangeMessage],
	);

	const renderErrorBubble = useCallback(() => {
		return (
			<BubbleContainer>
				<StyledDescription>{loadingError}</StyledDescription>
				<StyledSubDescription mb={1}>{tryAgainPlease}</StyledSubDescription>
				<Grid2 container spacing={1 / 2} direction="column">
					<Grid2>
						<Button
							sx={{whiteSpace: 'normal'}}
							data-tag-id={getOptionDataTagId(
								tryAgain,
								ScheduleScriptOptionTypeEnum.RETRY,
							)}
							variant="contained"
							fullWidth
							onClick={handleRetryRequest}>
							{tryAgain}
						</Button>
					</Grid2>
					<Grid2>
						<Button
							sx={{whiteSpace: 'normal'}}
							data-tag-id={getOptionDataTagId(
								goBack,
								ScheduleScriptOptionTypeEnum.RETRY,
							)}
							variant="outlined"
							fullWidth
							onClick={handleRestartConversation}>
							{goBack}
						</Button>
					</Grid2>
				</Grid2>
			</BubbleContainer>
		);
	}, [handleRetryRequest, handleRestartConversation]);

	return (
		<Fragment>
			<StyledContainer ref={containerRef}>
				{renderDefaultBubble(
					{
						description: greetings,
						answerOptions: [],
					},
					-1,
				)}
				{messages.length > 0
					? messages.map((msg, msgIdx) => (
							<Fragment key={msgIdx}>
								{renderDefaultBubble(msg, msgIdx)}
								{renderClientBubble(msgIdx)}
							</Fragment>
						))
					: null}
				{isPending ? (
					<BubbleContainer>
						<DotLoading />
					</BubbleContainer>
				) : null}
				{isError && (error as APIError).statusCode !== 500 ? renderErrorBubble() : null}
			</StyledContainer>
			<AlertModal
				open={openErrorModal}
				onClose={handleCloseErrorModal}
				description={serverError}
				title={serverErrorTitle}
				headerTitle={notice}
				buttonLabel={close}
				variant="info"
			/>
			{scheduledBooking ? (
				<HealthDuplicityDialog
					open={checkBookingModal === CheckBookingModalEnum.DUPLICITY_STATE}
					setOpen={() => {
						resetBookingDataSpecialties();
						setCheckBookingModal(CheckBookingModalEnum.NONE);
					}}
					isScheduleScript
					scheduledBooking={scheduledBooking as ParsedHealthAppointment}
				/>
			) : null}
		</Fragment>
	);
}
